// I quattro pezzi che servono già adesso. La libreria vera nasce con la Fase 3,
// quando ci saranno schermate a dirle che forma deve avere (§ 03.1).

import { useId, useState } from 'react';
import type { ErroreApi } from '../lib/api.js';
import { CONTROLLA_PRIMA, esitoDelGuasto } from '../lib/esiti.js';

export function Campo({
  etichetta,
  ...resto
}: { etichetta: string } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <label className="campo">
      <span>{etichetta}</span>
      <input {...resto} />
    </label>
  );
}

/**
 * ⭐⭐ **UN CAMPO PASSWORD CHE SI PUÒ GUARDARE** — 18/09/2026, richiesta sua:
 * 🗣️ *«quando ho fatto la login non si riesce a vedere la password che si
 *   inserisce»*.
 *
 * ⚖️ **Perché non è un vezzo, ed è il primo accesso a dirlo.** Una password la
 * si sceglie **qui**, e la si batte due volte: su un telefono, con la
 * correzione automatica di mezzo, battere alla cieca una cosa che non si sa
 * ancora a memoria è il modo di restare fuori senza capire perché — e il
 * rifiuto che si legge è sempre lo stesso, *«email o password non corretti»*,
 * che di proposito non distingue le due (⇒ `auth/routes.ts`).
 *
 * 🔒 **Parte COPERTA, e si scopre solo se qualcuno lo chiede.** Il verso
 * opposto — mostrarla e lasciar nascondere — regalerebbe la password a chi
 * passa dietro le spalle nell'istante in cui si batte, che è esattamente
 * l'istante in cui non ci si guarda intorno.
 *
 * ⛔ E lo stato **non si ricorda** da una schermata all'altra: è una scelta per
 * quella volta, in quel posto. Un «mostra password» che resta acceso per sempre
 * è un campo scoperto che nessuno ha più deciso.
 */
export function CampoPassword({
  etichetta,
  ...resto
}: { etichetta: string } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>): React.JSX.Element {
  const [scoperta, setScoperta] = useState(false);
  const id = useId();

  return (
    <label className="campo campo-password" htmlFor={id}>
      <span>{etichetta}</span>
      <span className="con-occhio">
        <input {...resto} id={id} type={scoperta ? 'text' : 'password'} />
        {/*
          ⚠️ `type="button"`: dentro un modulo, un bottone senza tipo **invia** —
          e qui vorrebbe dire tentare l'accesso premendo «Mostra».
          ⚖️ È una parola e non un disegnino: un occhio barrato lo si guarda due
          volte per capire se vuol dire «adesso si vede» o «premi per vedere».
        */}
        <button
          type="button"
          className="piatto"
          aria-pressed={scoperta}
          onClick={() => setScoperta((v) => !v)}
        >
          {scoperta ? 'Nascondi' : 'Mostra'}
        </button>
      </span>
    </label>
  );
}

/**
 * Un errore si mostra con le parole che l'API ha già scritto in italiano, e con
 * il suo identificativo di richiesta sotto: è quello che lega ciò che è
 * successo a schermo alla riga nel registro del server.
 */
export function Guasto({ errore }: { errore: unknown }): React.JSX.Element | null {
  if (!errore) return null;
  // 🔄 26/09/2026 — la linea caduta non si dice più «Failed to fetch»: si dice
  // che non si sa com'è andata, e di controllare prima di rifare (lib/esiti.ts).
  const e = esitoDelGuasto(errore);
  const testo = e.tipo === 'forse' ? `${e.testo} ${CONTROLLA_PRIMA}` : (errore as ErroreApi).message;
  return (
    <p className="guasto" role="alert">
      {testo}
    </p>
  );
}

/** Mai una pagina bianca: uno scheletro dice «sto arrivando» (§ 03.7). */
export function Attesa({ cosa }: { cosa: string }): React.JSX.Element {
  return <p className="attesa">Sto leggendo {cosa}…</p>;
}

export function Vuoto({ titolo, perché }: { titolo: string; perché: string }): React.JSX.Element {
  return (
    <div className="vuoto">
      <strong>{titolo}</strong>
      <p className="tenue">{perché}</p>
    </div>
  );
}
